import { Link } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { NAVIGATION } from '@/app/navigation'
import { can } from '@/auth/permissions'
import type { CurrentUser } from '@/auth/permissions'
import { formatMoney, type Amount } from '@/format/money'
import { formatCell } from './DataTable'
import type { ColumnDescriptor } from './types'

/**
 * Что показать в ячейке вместо сырого значения (аудит интерфейса, октябрь 2026).
 *
 * Список и карточка печатали то, что лежит в базе: UUID там, где у записи есть имя, и код
 * (`partially_paid`) там, где у состояния есть слово. Одна функция на оба экрана — иначе
 * карточка и строка списка назвали бы одну и ту же сделку по-разному.
 */

/** Ключ строки, под которым сервер кладёт название ссылки (`ListCatalog.LABEL_SUFFIX`). */
export function labelKey(column: string): string {
  return `${column}__label`
}

/**
 * Ключ перевода значения: `value.<сущность>.<колонка>.<код>`.
 *
 * По сущности, а не общий по колонке: в русском род — лид «Новый», вакансия «Новая»,
 * счёт «Оплачен», сделка «Открыта».
 */
export function valueKey(entity: string, column: string, code: string): string {
  return `value.${entity}.${column}.${code}`
}

/**
 * Текст значения.
 *
 * Порядок: название ссылки → перевод кода → форматирование. Неизвестный код показывается
 * как есть, а не пустотой: `partially_paid` на экране виден и чинится переводом, пустая
 * ячейка выглядит незаполненной и живёт годами (та же логика, что у непереведённого ключа).
 */
export function displayText(
  values: Record<string, unknown>,
  column: string,
  descriptor: ColumnDescriptor | undefined,
  entity: string | undefined,
  t: (key: string) => string,
  locale: string,
): string {
  const value = values[column]
  if (descriptor?.reference) {
    const label = values[labelKey(column)]
    // Ссылка без названия — цель удалена или скрыта: показываем ключ, а не пустоту,
    // иначе заполненное поле выглядело бы незаполненным.
    if (typeof label === 'string' && label !== '') return label
  }
  if (descriptor?.money) {
    // The one money formatter (#91): the list, the card and the board print a sum alike.
    const key = descriptor.money.currencyKey
    const currency = key ? values[key] : null
    const amount: Amount = typeof value === 'number' || typeof value === 'string' ? value : null
    return formatMoney(amount, typeof currency === 'string' ? currency : null, locale)
  }
  if (entity && typeof value === 'string' && descriptor?.type === 'text') {
    const key = valueKey(entity, column, value)
    const translated = t(key)
    if (translated !== key) return translated
  }
  return formatCell(value, locale)
}

/**
 * Тон статусной плашки по коду (дизайн: «цвет — только для смысла»).
 *
 * Один словарь на все сущности: «оплачен», «выиграна», «проведён» — один смысл «готово»,
 * «просрочен», «проиграна», «ошибка» — «проблема». Код без тона — нейтральная плашка,
 * а не догадка: цвет, поставленный наугад, учит не верить цвету.
 */
const STATUS_TONE: Record<string, 'success' | 'warning' | 'danger' | 'info'> = {
  paid: 'success', won: 'success', accepted: 'success', active: 'success', completed: 'success',
  posted: 'success', approved: 'success', delivered: 'success', signed: 'success', matched: 'success',
  confirmed: 'success', registered: 'success', hired: 'success', done: 'success', received: 'success',
  shipped: 'success',
  overdue: 'danger', lost: 'danger', rejected: 'danger', failed: 'danger', abandoned: 'danger',
  partially_paid: 'warning', pending: 'warning', in_progress: 'warning', on_hold: 'warning',
  probation: 'warning', on_leave: 'warning', counting: 'warning', picking: 'warning',
  in_transit: 'warning', expired: 'warning',
  sent: 'info', new: 'info', qualified: 'info', submitted: 'info', open: 'info', converted: 'info',
}

/** Колонки, значения которых — состояние записи, и показываются плашкой. */
const STATUS_COLUMNS = new Set(['status', 'edi_status'])

/** Путь к карточке записи списка `entity`, если раздел пользователю открыт. */
export function cardHref(user: CurrentUser | null, entity: string, id: string): string | null {
  const item = NAVIGATION.flatMap((section) => section.items)
    .find((candidate) => candidate.listEntity === entity)
  // Ссылка в раздел, которого нет в меню пользователя, привела бы на экран отказа:
  // название показываем, перехода не обещаем.
  if (!item || !can(user, item.resource, 'read')) return null
  return `${item.path}/${id}`
}

/**
 * Значение ячейки: текст, а у ссылки на запись с карточкой — переход на неё.
 *
 * Переход — обычная ссылка, а не обработчик клика: её можно открыть в новой вкладке, а
 * клиента сделки чаще смотрят именно так, не теряя список.
 */
export function CellValue({
  values,
  column,
  descriptor,
  entity,
}: {
  values: Record<string, unknown>
  column: string
  descriptor: ColumnDescriptor | undefined
  entity: string | undefined
}) {
  const { t, locale, user } = useApp()
  const text = displayText(values, column, descriptor, entity, t, locale)
  const raw = values[column]
  if (STATUS_COLUMNS.has(column) && typeof raw === 'string' && text !== '') {
    const tone = STATUS_TONE[raw]
    // Точка и слово вместе: цвет не единственный носитель смысла (правило дизайна).
    return (
      <span className={tone ? `badge b-${tone}` : 'badge'}>
        <span className="dot" aria-hidden="true" />
        {text}
      </span>
    )
  }
  const target = descriptor?.reference?.entity
  const id = values[column]
  if (target && typeof id === 'string' && text !== '') {
    const href = cardHref(user, target, id)
    if (href) return <Link to={href} className="cell-link">{text}</Link>
  }
  return <>{text}</>
}
